import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, ProgressBar, Spinner, Avatar, StatusBadge, WeightBadge, TimelineBadge } from "@/components/UI";
import useMeta from "@/hooks/useMeta";
import TaskModal from "@/components/TaskModal";
import {
  FolderKanban, CheckCircle2, AlertTriangle, Rocket, Users, Zap, Clock, ListTodo, BellRing,
  TrendingUp, ArrowDownRight, ClipboardCheck, RefreshCw,
} from "lucide-react";
import { AreaChart, Area, BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [perf, setPerf] = useState(null);
  const [team, setTeam] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const loadMyTasks = () => {
    if (user?.id) api.get("/tasks", { params: { assignee_id: user.id } }).then((r) => setMyTasks(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    api.get("/users").then((r) => setTeam(r.data)).catch(() => {});
    if (isAdmin) {
      api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
      api.get("/dashboard/workload").then((r) => setWorkload(r.data)).catch(() => {});
      api.get("/dashboard/performance").then((r) => setPerf(r.data)).catch(() => {});
    }
    loadMyTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, user?.id]);

  if (!isAdmin) return <EmployeeDashboard user={user} team={team} />;
  if (!summary) return <Spinner />;

  const s = summary;
  const sprint = s.active_sprint;

  // Task milik admin di sprint aktif (belum done) — untuk card "My Tasks" duplikat
  const mySprintOpenTasks = sprint
    ? myTasks
        .filter((t) => t.sprint_id === sprint.sprint.id && !["cancelled", "replaced", "backlog", "planned", "done"].includes(t.status))
        .sort((a, b) => {
          const la = a.timeline_status === "late" ? 0 : 1;
          const lb = b.timeline_status === "late" ? 0 : 1;
          if (la !== lb) return la - lb;
          return (a.deadline || "9999").slice(0, 10).localeCompare((b.deadline || "9999").slice(0, 10));
        })
    : [];

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-slate-400">Ringkasan project, sprint, workload & performance tim.</p>
        </div>
        <a href="#admin-personal-workspace" className="flex items-center gap-1 text-sm text-sky-400 hover:underline self-end sm:self-auto" title="Lihat My Workspace">
          <ArrowDownRight size={14} /> My Workspace
        </a>
      </div>

      <StatusBar user={user} team={team} onEdit={() => navigate("/profile")} />

      {/* Top KPI row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testid="kpi-projects" icon={FolderKanban} color="#D1D5DB" label="Total Project" value={s.projects.total}
          sub={`${s.projects.active} aktif · ${s.projects.completed} selesai`} onClick={() => navigate("/projects")} />
        <StatCard testid="kpi-late" icon={AlertTriangle} color="#F43F5E" label="Project Terlambat" value={s.projects.late}
          sub="ada task overdue" onClick={() => navigate("/projects")} />
        <StatCard testid="kpi-approval" icon={BellRing} color="#9CA3AF" label="Waiting Approval" value={s.tasks.waiting_approval}
          sub="urgent task perlu bobot" onClick={() => navigate("/approvals")} />
        <StatCard testid="kpi-team" icon={Users} color="#D1D5DB" label="Team Members" value={s.team_size}
          sub={`${s.tasks.in_progress} task in progress`} onClick={() => navigate("/sprint")} />
      </div>

      {/* Sprint overview */}
      {sprint && (
        <Card className="p-5" data-testid="sprint-summary-card">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Rocket size={18} className="text-slate-400" />
              <h2 className="font-heading text-lg font-semibold">{sprint.sprint.name}</h2>
              <span className="rounded-md bg-slate-500/15 px-2 py-0.5 text-[11px] font-semibold text-slate-400">ACTIVE</span>
            </div>
            <button onClick={() => navigate("/sprint")} className="text-sm text-slate-400 hover:underline" data-testid="goto-sprint">
              Buka Sprint Board →
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <SprintMetric icon={Zap} color="#D1D5DB" label="Total Bobot" value={sprint.totals.total_allocated} unit="pts" />
            <SprintMetric icon={CheckCircle2} color="#D1D5DB" label="Bobot Selesai" value={sprint.totals.total_completed} unit="pts" />
            <SprintMetric icon={ListTodo} color="#9CA3AF" label="Tersisa" value={sprint.totals.total_remaining} unit="pts" />
            <SprintMetric icon={AlertTriangle} color="#F43F5E" label="Overdue" value={sprint.totals.overdue_tasks} unit="task" />
          </div>

          <div className="mt-5">
            <div className="mb-1.5 flex justify-between text-xs text-slate-400">
              <span>Progress sprint (bobot selesai)</span>
              <span className="font-mono text-slate-400">
                {sprint.totals.total_completed}/{sprint.totals.total_allocated} pts
              </span>
            </div>
            <ProgressBar value={sprint.totals.total_completed} max={sprint.totals.total_allocated} color="#D1D5DB" height={10} />
          </div>

          {sprint.totals.sprint_over_capacity > 0 && (
            <div className="mt-4 flex items-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300">
              <AlertTriangle size={16} />
              Sprint Over Capacity: <b>+{sprint.totals.sprint_over_capacity} poin</b> di atas kapasitas tim.
            </div>
          )}
        </Card>
      )}

      {/* My Tasks — duplikat card dari My Workspace, ditaruh di dashboard global admin */}
      {sprint && (
        <MyTasksCard tasks={mySprintOpenTasks} sprint={sprint.sprint} onSaved={loadMyTasks} testid="my-tasks-card-admin" />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Team workload */}
        <Card className="p-5 lg:col-span-2" data-testid="team-workload-card">
          <h2 className="mb-4 font-heading text-lg font-semibold">Team Workload — Sprint Aktif</h2>
          <div className="space-y-4">
            {workload?.employees?.map((e) => (
              <WorkloadRow key={e.user_id} e={e} onClick={() => navigate("/sprint")} />
            ))}
            {!workload?.employees?.length && <p className="text-sm text-slate-500">Belum ada data workload.</p>}
          </div>
        </Card>

        {/* Team performance trend */}
        <Card className="p-5" data-testid="performance-trend-card">
          <h2 className="mb-1 font-heading text-lg font-semibold">Performance Tim</h2>
          <p className="mb-3 text-xs text-slate-500">Tren mingguan (bobot selesai / dialokasikan)</p>
          {perf?.series?.length ? (
            <>
              <div className="mb-3 flex items-end gap-2">
                <span className="font-heading text-4xl font-bold text-slate-400 tabular-nums">
                  {perf.series[perf.series.length - 1]?.performance ?? "—"}
                </span>
                <span className="mb-1 text-sm text-slate-400">% minggu ini</span>
              </div>
              <ResponsiveContainer width="100%" height={140}>
                <AreaChart data={perf.series}>
                  <defs>
                    <linearGradient id="perfGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#E5E7EB" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#E5E7EB" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(w) => `W${w.split('W')[1]}`} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }} />
                  <Area type="monotone" dataKey="performance" stroke="#E5E7EB" strokeWidth={2} fill="url(#perfGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </>
          ) : (
            <p className="text-sm text-slate-500">Belum ada data.</p>
          )}
          <button onClick={() => navigate("/performance")} className="mt-3 w-full text-sm text-slate-400 hover:underline" data-testid="goto-performance">
            Lihat Laporan →
          </button>
        </Card>
      </div>

      {/* Personal Workspace (Admin's own tasks) */}
      <div id="admin-personal-workspace" className="pt-4 border-t border-white/10" data-testid="admin-personal-workspace">
        <h2 className="mb-4 font-heading text-xl font-semibold">My Workspace</h2>
        <EmployeeDashboard user={user} team={team} showStatusBar={false} showHeader={false} />
      </div>
    </div>
  );
}

const TASK_LIMIT = 3;
const PERF_MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

function EmployeeDashboard({ user, team, showStatusBar = true, showHeader = true }) {
  const navigate = useNavigate();
  const [sprintData, setSprintData] = useState(null);
  const [perf, setPerf] = useState(null);
  const [myTasks, setMyTasks] = useState([]);
  const [ready, setReady] = useState(false);

  const loadTasks = () => {
    if (!user) return;
    api.get("/tasks", { params: { assignee_id: user.id } }).then((r) => setMyTasks(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    if (!user) return;
    api.get("/sprints/active").then((r) => setSprintData(r.data))
      .catch(() => {})
      .finally(() => setReady(true));
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Performance per sprint bulan berjalan (sama seperti laporan)
  useEffect(() => {
    if (!user) return;
    const now = new Date();
    api.get("/dashboard/performance", { params: { user_id: user.id, type: "sprint", year: now.getFullYear(), month: now.getMonth() + 1 } })
      .then((r) => setPerf(r.data)).catch(() => {});
  }, [user?.id]);

  if (!ready) return <Spinner />;

  const sprint = sprintData?.sprint;
  const me = (sprintData?.employees || []).find((e) => e.user_id === user?.id);
  const totalAllocated = (perf?.series || []).reduce((s, p) => s + (p.allocated || 0), 0);
  const totalOutput = (perf?.series || []).reduce((s, p) => s + (p.output || 0), 0);
  const overallPerf = totalAllocated > 0 ? Math.round((totalOutput / totalAllocated) * 100) : null;

  const todayStr = new Date().toLocaleDateString("sv-SE");
  const activeSprintId = sprintData?.sprint?.id;

  // Semua task saya di sprint aktif (untuk stat cards)
  const allSprintTasks = activeSprintId
    ? myTasks.filter((t) => t.sprint_id === activeSprintId && !["cancelled", "replaced", "backlog", "planned"].includes(t.status))
    : [];
  const cntAssigned = allSprintTasks.length;
  const cntInProgress = allSprintTasks.filter((t) => t.status === "in_progress").length;
  const cntCleared = allSprintTasks.filter((t) => t.status === "done").length;
  const cntOverdue = allSprintTasks.filter((t) => t.status !== "done" && t.timeline_status === "late").length;

  // Task saya (belum selesai) — terlambat dulu, lalu deadline terdekat; dibatasi TASK_LIMIT
  const myOpenTasks = allSprintTasks.filter((t) => t.status !== "done");
  const taskSaya = [...myOpenTasks].sort((a, b) => {
    const la = a.timeline_status === "late" ? 0 : 1;
    const lb = b.timeline_status === "late" ? 0 : 1;
    if (la !== lb) return la - lb;
    const da = a.deadline ? a.deadline.slice(0, 10) : "9999";
    const db = b.deadline ? b.deadline.slice(0, 10) : "9999";
    return da.localeCompare(db);
  });
  const sprintOverdue = cntOverdue;

  // Sprint progress (bobot)
  const committed = me?.allocated || 0;
  const completedPts = me?.completed || 0;
  const remainingPts = Math.max(0, committed - completedPts);
  const sprintPct = committed > 0 ? Math.min(100, Math.round((completedPts / committed) * 100)) : 0;

  // Chart performance: per sprint dalam bulan terpilih
  const perfSeries = perf?.series || [];

  return (
    <div className={showHeader ? "mx-auto max-w-6xl space-y-6" : "space-y-6"} data-testid="dashboard-page-employee">
      {showHeader && (
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-slate-400">
            {sprint ? `Here's your work overview for Sprint ${sprint.name}` : "Here's your work overview — no active sprint."}
          </p>
        </div>
      )}

      {showStatusBar && (
        <StatusBar user={user} team={team} onEdit={() => navigate("/profile")} />
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="my-task-stats">
        <TaskStatCard icon={ClipboardCheck} color="#3B82F6" label="My Tasks" value={cntAssigned} sub="tasks assigned" />
        <TaskStatCard icon={RefreshCw} color="#8B5CF6" label="In Progress" value={cntInProgress} sub="tasks ongoing" />
        <TaskStatCard icon={CheckCircle2} color="#14B8A6" label="Cleared" value={cntCleared} sub="tasks finished" />
        <TaskStatCard icon={AlertTriangle} color="#F43F5E" label="Overdue" value={cntOverdue} sub="task overdue" danger={cntOverdue > 0} />
      </div>

      {/* Task Saya */}
      <MyTasksCard tasks={taskSaya} sprint={sprint} onSaved={loadTasks} />

      <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2">
        {/* Sprint Progress */}
        <Card className="p-5" data-testid="sprint-progress-card">
          <div className="mb-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Rocket size={18} className="text-slate-400" />
              <h2 className="font-heading text-lg font-semibold">Sprint Progress</h2>
            </div>
            {sprint && (
              <span className="text-xs text-slate-500">
                W{sprint.week_number} · {new Date(sprint.start_date).toLocaleDateString("id-ID", { day: "2-digit", month: "short" })}
                {" – "}
                {new Date(sprint.end_date).toLocaleDateString("id-ID", { day: "2-digit", month: "short" })}
              </span>
            )}
          </div>
          {me ? (
            <>
              <div className="flex items-center gap-6">
                {/* Donut */}
                <div className="relative h-28 w-28 shrink-0">
                  <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#1E293B" strokeWidth="10" />
                    <circle
                      cx="50" cy="50" r="42" fill="none" stroke="#38BDF8" strokeWidth="10" strokeLinecap="round"
                      strokeDasharray={`${(sprintPct / 100) * 263.9} 263.9`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-heading text-xl font-bold tabular-nums text-slate-100">{sprintPct}%</span>
                    <span className="text-[10px] text-slate-500">Completed</span>
                  </div>
                </div>
                <div className="flex-1 space-y-1.5">
                  <p className="text-sm text-slate-300">
                    <span className="font-mono font-bold tabular-nums text-slate-100">{completedPts} / {committed} pts</span>{" "}
                    <span className="text-slate-500">Completed</span>
                  </p>
                  <p className="text-sm text-slate-300">
                    <span className="font-mono font-bold tabular-nums text-slate-100">{remainingPts} pts</span>{" "}
                    <span className="text-slate-500">Remaining</span>
                  </p>
                  <p className="text-sm">
                    <span className={`font-mono font-bold tabular-nums ${sprintOverdue > 0 ? "text-rose-400" : "text-slate-100"}`}>
                      {sprintOverdue} task
                    </span>{" "}
                    <span className={sprintOverdue > 0 ? "text-rose-400" : "text-slate-500"}>Overdue</span>
                  </p>
                  <div className="pt-1.5">
                    <ProgressBar value={completedPts} max={committed || 1} color="#38BDF8" height={8} />
                  </div>
                </div>
              </div>
              {/* Legend */}
              <div className="mt-5 grid grid-cols-2 gap-y-2 border-t border-slate-800 pt-4 text-xs text-slate-400 sm:grid-cols-4">
                <span><span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-slate-500" />Committed <span className="block pl-3.5 font-mono tabular-nums text-slate-300">{committed} pts</span></span>
                <span><span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-emerald-400" />Completed <span className="block pl-3.5 font-mono tabular-nums text-slate-300">{completedPts} pts</span></span>
                <span><span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-sky-500" />Remaining <span className="block pl-3.5 font-mono tabular-nums text-slate-300">{remainingPts} pts</span></span>
                <span><span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-rose-500" />Overdue <span className="block pl-3.5 font-mono tabular-nums text-slate-300">{sprintOverdue} task</span></span>
              </div>
            </>
          ) : (
            <p className="py-6 text-center text-sm text-slate-500">Belum ada task di sprint aktif.</p>
          )}
        </Card>

        {/* Performance */}
        <Card className="flex h-full flex-col p-5" data-testid="my-performance-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <TrendingUp size={18} className="text-slate-400" />
              <h2 className="font-heading text-lg font-semibold">Performance — per Sprint</h2>
            </div>
            <button onClick={() => navigate("/performance")} className="text-xs text-sky-400 hover:underline" data-testid="goto-performance">
              View details →
            </button>
          </div>
          {perfSeries.length ? (
            <>
              <div className="mt-3 flex items-end gap-3">
                <span className="font-heading text-4xl font-bold tabular-nums text-slate-100">{overallPerf ?? "—"}%</span>
                <div className="mb-1">
                  <p className="text-xs text-slate-500">Bulan {PERF_MONTHS[new Date().getMonth()]} {new Date().getFullYear()}</p>
                </div>
              </div>
              <div className="mt-2 flex-1">
                <ResponsiveContainer width="100%" height="100%" minHeight={150}>
                  <BarChart data={perfSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                    <XAxis dataKey="sprint" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }}
                      formatter={(v, name) => (name === "Performance (%)" ? [`${v ?? 0}%`, "Performance"] : [`${v ?? 0} pts`, name])}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="output" name="Output (pts)" fill="#22C55E" radius={[4, 4, 0, 0]} maxBarSize={24} />
                    <Bar dataKey="allocated" name="Allocated (pts)" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={24} />
                    <Bar dataKey="performance" name="Performance (%)" fill="#F59E0B" radius={[4, 4, 0, 0]} maxBarSize={24} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <p className="py-6 text-center text-sm text-slate-500">Belum ada data performance untuk bulan ini.</p>
          )}
        </Card>
      </div>

    </div>
  );
}

function MyTasksCard({ tasks, sprint, onSaved, testid = "my-tasks-card" }) {
  const navigate = useNavigate();
  const meta = useMeta();
  const [taskModal, setTaskModal] = useState({ open: false, mode: "view", task: null });
  const shown = tasks.slice(0, TASK_LIMIT);
  const hidden = tasks.length - shown.length;

  return (
    <>
      <Card className="p-5" data-testid={testid}>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListTodo size={18} className="text-slate-400" />
            <h2 className="font-heading text-lg font-semibold">My Tasks</h2>
            {tasks.length > 0 && (
              <span className="rounded-md bg-slate-500/15 px-2 py-0.5 text-[11px] font-semibold text-slate-400">
                {tasks.length} remaining
              </span>
            )}
          </div>
          <button onClick={() => navigate("/kanban")} className="text-xs text-slate-400 hover:text-slate-200 hover:underline" data-testid="goto-kanban">
            Kanban →
          </button>
        </div>
        {tasks.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500">
            {sprint ? "Tidak ada task tersisa di sprint ini. Kerja bagus!" : "Belum ada sprint aktif."}
          </p>
        ) : (
          <div className="divide-y divide-slate-800/60">
            {shown.map((t) => (
              <div
                key={t.id}
                onClick={() => setTaskModal({ open: true, mode: "view", task: t })}
                className="flex cursor-pointer items-center gap-4 py-2.5 hover:bg-white/[0.02]"
                data-testid={`my-task-${t.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-200">{t.title}</p>
                  <p className="truncate text-xs text-slate-500">
                    {t.project?.name || "Internal"}
                    {t.phase?.name ? ` · ${t.phase.name}` : ""}
                  </p>
                </div>
                {t.timeline_status === "late" && (
                  <span className="rounded-md bg-rose-500/15 px-2 py-0.5 text-[11px] font-semibold text-rose-400">Terlambat</span>
                )}
                <WeightBadge weight={t.weight} />
                {t.deadline && <TimelineBadge date={t.deadline} />}
                <StatusBadge status={t.status} />
              </div>
            ))}
          </div>
        )}
        {hidden > 0 && (
          <button
            onClick={() => navigate("/kanban")}
            className="mt-3 w-full border-t border-slate-800/60 pt-3 text-center text-xs text-slate-400 hover:text-slate-200"
            data-testid="my-tasks-more"
          >
            …dan {hidden} task lainnya — lihat semua di Kanban →
          </button>
        )}
      </Card>
      <TaskModal
        open={taskModal.open}
        mode={taskModal.mode}
        task={taskModal.task}
        meta={meta}
        onClose={() => setTaskModal({ open: false, mode: "view", task: null })}
        onSaved={onSaved}
      />
    </>
  );
}

function StatCard({ icon: Icon, color, label, value, sub, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="group text-left">
      <Card className="relative overflow-hidden p-5 transition-all group-hover:-translate-y-1 group-hover:border-slate-700/80">
        <div className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: color }} />
        <Icon size={72} className="pointer-events-none absolute -right-4 -top-4 opacity-[0.07]" style={{ color }} strokeWidth={1.5} />
        <div className="relative mb-3 flex h-9 w-9 items-center justify-center rounded-xl" style={{ backgroundColor: color + "1A", color }}>
          <Icon size={17} />
        </div>
        <p className="relative font-heading text-3xl font-bold tabular-nums">{value}</p>
        <p className="relative mt-1 text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
        <p className="relative mt-2 text-xs text-slate-500">{sub}</p>
      </Card>
    </button>
  );
}

function TaskStatCard({ icon: Icon, color, label, value, sub, danger }) {
  return (
    <Card className="flex items-center gap-3 p-4">
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
        style={{ backgroundColor: `${color}1A`, color }}
      >
        <Icon size={19} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-slate-400">{label}</p>
        <p className={`font-heading text-2xl font-bold tabular-nums ${danger ? "text-rose-400" : "text-slate-100"}`}>{value}</p>
        <p className="truncate text-[11px] text-slate-500">{sub}</p>
      </div>
    </Card>
  );
}

function SprintMetric({ icon: Icon, color, label, value, unit }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
      <div className="mb-1 flex items-center gap-1.5 text-xs text-slate-400">
        <Icon size={13} style={{ color }} /> {label}
      </div>
      <p className="font-mono text-2xl font-bold tabular-nums" style={{ color }}>
        {value} <span className="text-sm text-slate-500">{unit}</span>
      </p>
    </div>
  );
}

function WorkloadRow({ e, onClick }) {
  const perfColor = e.performance == null ? "#64748B" : e.performance >= 90 ? "#D1D5DB" : e.performance >= 60 ? "#E5E7EB" : "#F43F5E";
  return (
    <div className="cursor-pointer" onClick={onClick} data-testid={`workload-row-${e.user_id}`}>
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Avatar src={e.avatar_url} name={e.user_name} size={28} />
          <span className="text-sm font-medium text-slate-200">{e.user_name}</span>
          {e.is_over_capacity && <span className="rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-rose-400">OVER +{e.over_capacity}</span>}
          {e.extra_contribution > 0 && <span className="rounded bg-slate-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-slate-400">EXTRA +{e.extra_contribution}</span>}
        </div>
        <div className="flex items-center gap-3 font-mono text-xs tabular-nums">
          <span className="text-slate-400">{e.completed ?? 0}/{e.allocated ?? 0} pts</span>
          <span style={{ color: perfColor }}>{e.performance == null ? "N/A" : e.performance + "%"}</span>
        </div>
      </div>
      <ProgressBar value={e.completed ?? 0} max={e.allocated || 1} color={e.is_over_capacity ? "#F43F5E" : "#D1D5DB"} height={6} />
    </div>
  );
}

function StatusBar({ user, team, onEdit }) {
  const all = user ? [user, ...team.filter((u) => u.id !== user.id)] : team;
  const withFeeling = all.filter((u) => u.feeling?.trim());
  if (withFeeling.length === 0) return null;

  return (
    <div className="pb-2" data-testid="dashboard-status-bar">
      <div className="flex gap-6 overflow-x-auto pb-2">
        {withFeeling.map((u) => (
          <div
            key={u.id}
            onClick={u.id === user?.id ? onEdit : undefined}
            className={`flex min-w-[100px] flex-col items-center ${u.id === user?.id ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <div className="flex h-16 w-[130px] items-end justify-center">
              <div className="relative max-w-[130px]">
                <div className="overflow-hidden rounded-2xl bg-slate-700 px-3.5 py-2 text-center text-xs leading-tight text-slate-100 shadow-md">
                  <p className="line-clamp-2">{u.feeling.trim()}</p>
                </div>
                <span className="absolute left-1/2 top-full -translate-x-1/2 border-x-[7px] border-t-[8px] border-x-transparent border-t-slate-700" />
              </div>
            </div>
            <Avatar src={u.avatar_url} name={u.name} size={72} className="mt-2.5" />
            <span className="mt-1.5 max-w-[120px] truncate text-sm font-medium text-slate-300">{u.id === user?.id ? "Catatan Anda" : u.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
